import {ApolloClient, HttpLink, InMemoryCache, InMemoryCacheConfig, split} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {onError} from '@apollo/client/link/error';
import {OktaAuth} from '@okta/okta-auth-js';
import {useCallback, useLayoutEffect, useState} from 'react';
import { OktaTokenManager } from '../manager/OktaTokenManager';

const apiUrl = `${process.env.REACT_APP_API_URL}/api/graphql`;
const tosUrl = `${process.env.REACT_APP_TOS_URL}/api/graphql`;

const APOLLO_IN_MEMORY_CACHE_CONFIG: InMemoryCacheConfig = {
    typePolicies: {
        Offer: {
            keyFields: ['id'],
        },
        Referral: {
            keyFields: ['id'],
        },
        ProviderUserInfoDTO: {
            keyFields: ['oktaId'],
        }
    }
};


const newReAuthenticateLink = (tokenManager: OktaTokenManager) => {
    return onError(({networkError,}) => {
        if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
            console.warn("Access token rejected by server, will re-authenticate");
            tokenManager.loginRedirect();
        }
    });
};
const newBearerTokenLink = (tokenManager: OktaTokenManager) => {
    return setContext(async (_, {headers}) => {
        // Check if token is about to expire before each request
        if (await tokenManager.isTokenExpiring()) {
            try {
                // Try to refresh the token proactively
                const newToken = await tokenManager.refreshToken();
                return {
                    headers: {
                        ...headers,
                        Authorization: newToken ? `Bearer ${newToken}` : "",
                    }
                };
            } catch (error) {
                console.error('[Apollo Request] Proactive token refresh failed:', error);
                // Fall back to login redirect if not locked
                if (!tokenManager.isRedirectLocked()) {
                    tokenManager.loginRedirect();
                }
            }
        }
        
        // Get the current token
        const token = await tokenManager.getAccessToken();
        return {
            headers: {
                ...headers,
                Authorization: token ? `Bearer ${token}` : "",
            }
        }
    });
};
const bearerWithReauthenticateLink = (tokenManager: OktaTokenManager) =>
    newBearerTokenLink(tokenManager)
        .concat(
            newReAuthenticateLink(tokenManager));

const operationAwareLink = () => split(
    (operation) => operation.operationName === 'checkMyTosStatus',
    new HttpLink({uri: tosUrl}),
    new HttpLink({uri: apiUrl})
)


export const useAuthenticatedApolloClient = (oktaAuth: OktaAuth): ApolloClient<any> | null => {
    const [apolloInstance, setApolloInstance] = useState<ApolloClient<any> | null>(null);
    const [tokenManager] = useState(() => new OktaTokenManager(oktaAuth));

    const setUpApolloClient = useCallback(() => {
        const authState = oktaAuth.authStateManager.getAuthState();
        if (authState?.isAuthenticated && authState?.idToken) {
            if (apolloInstance === null) {
                setApolloInstance(new ApolloClient({
                    link: bearerWithReauthenticateLink(tokenManager)
                        .concat(operationAwareLink()),
                    cache: new InMemoryCache(APOLLO_IN_MEMORY_CACHE_CONFIG),
                }));
            } else {
                apolloInstance.setLink(bearerWithReauthenticateLink(tokenManager)
                    .concat(operationAwareLink())
                );
            }
        }
    }, [oktaAuth, apolloInstance, tokenManager]);

    useLayoutEffect(() => {
        setUpApolloClient();
        return oktaAuth.authStateManager.subscribe(setUpApolloClient);
    }, [oktaAuth, setUpApolloClient]);

    return apolloInstance;
};
